import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LightningBolt } from './GradientTracing';

const AdvancedLoadingAnimation = ({ message = "Processing..." }) => {
  // Marketing insights that cycle
  const insights = [
    "Analyzing social engagement metrics...",
    "Processing content performance data...",
    "Evaluating customer journey touchpoints...",
    "Calculating ROI across marketing channels...",
    "Mapping competitive positioning landscape...",
    "Segmenting audience demographics..."
  ];
  
  const [currentInsight, setCurrentInsight] = useState(0);
  
  // Cycle through insights
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentInsight(prev => (prev + 1) % insights.length);
    }, 3000);
    
    return () => clearInterval(timer);
  }, [insights.length]);

  // Define the nodes - static version
  const nodes = [
    { id: 'social', label: 'Social', position: { x: -180, y: -150 } },
    { id: 'content', label: 'Content', position: { x: -180, y: 150 } },
    { id: 'analytics', label: 'Analytics', position: { x: 180, y: -150 } },
    { id: 'sales', label: 'Sales', position: { x: 180, y: 150 } },
    { id: 'brand', label: 'Brand', position: { x: 0, y: -200 } },
  ];

  // Blinking status for particles (simple CSS-based animation)
  const [blink, setBlink] = useState(false);

  useEffect(() => {
    const blinkTimer = setInterval(() => {
      setBlink(prev => !prev);
    }, 1000);
    
    return () => clearInterval(blinkTimer);
  }, []);

  return (
    <div className="advanced-loading-container">
      <div className="animation-wrapper">
        <svg 
          viewBox="-250 -250 500 500" 
          width="100%" 
          height="100%"
          className="flow-animation"
        >
          {/* Connection lines (static) */}
          {nodes.map((node) => (
            <path
              key={`line-${node.id}`}
              d={`M ${node.position.x} ${node.position.y} L 0 0`}
              stroke="rgba(255, 165, 0, 0.6)"
              strokeWidth="2"
              fill="none"
            />
          ))}
          
          {/* Simple CSS-animated particles */}
          {nodes.map((node, i) => (
            <circle
              key={`particle-${node.id}`}
              className={`data-particle ${blink ? 'blink' : ''}`}
              cx={(node.position.x + 0) / 2}
              cy={(node.position.y + 0) / 2}
              r="3"
              fill="#FFCC00"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
          
          {/* Nodes (static) */}
          {nodes.map((node) => (
            <g key={`node-${node.id}`} transform={`translate(${node.position.x}, ${node.position.y})`}>
              <circle
                r="30"
                fill="#1e1e1e"
                stroke="rgba(255, 165, 0, 0.4)"
                strokeWidth="2"
              />
              <text
                textAnchor="middle"
                y="5"
                fill="white"
                fontSize="12"
                fontWeight="500"
              >
                {node.label}
              </text>
            </g>
          ))}
          
          {/* Center node (static) */}
          <circle
            cx="0"
            cy="0"
            r="50"
            fill="#1a1a1a"
            stroke="rgba(255, 165, 0, 0.8)"
            strokeWidth="3"
            className="center-node-pulse"
          />
        </svg>
        
        {/* Center lightning bolt */}
        <div className="center-icon">
          <LightningBolt width={60} height={80} colors={["#F1C40F", "#F39C12", "#E67E22"]} />
        </div>
      </div>
      
      <div className="insight-container">
        <AnimatePresence mode="wait">
          <motion.p 
            key={currentInsight}
            className="insight-text"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
          >
            {insights[currentInsight]}
          </motion.p>
        </AnimatePresence>
      </div>
      
      <p className="loading-text">{message}</p>
    </div>
  );
};

export default AdvancedLoadingAnimation; 